import type { ContractTypeCanHaveEndDate } from '@libs/data-access/entities'
import {
  ContractTypeCanHaveEndDateArray,
  APPRENTISSAGE,
  CDD,
  EXECUTIVE_OFFICER_AS_EMPLOYEE,
  EXECUTIVE_OFFICER_SELF_EMPLOYED,
  INTERN,
  type PayPeriodRecordMap
} from '@libs/data-access/entities'
import { accountService, locationService } from '@libs/data-access/queries'
import {
  Alert,
  Anchor,
  Flex,
  IconWrap,
  InformationCircleBoldIcon,
  InputWrap,
  Modal,
  Text,
  TextInput,
  Tooltip
} from '@libs/ui/ds'
import { BaseForm, Controller } from '@libs/ui/form'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { FormError } from '@spa/components/FormError'
import { FieldLabel } from '@spa/components/FormFields'
import { HRSynchronizedIcon } from '@spa/features'
import { QueriesRequestsStatus } from '@spa/features/Queries'
import { MatriculInfoIcon } from '@spa/features/SilaeHrSynchronization/components/MatriculInfoIcon/MatriculInfoIcon'
import { SilaeSyncFieldId } from '@spa/features/SilaeHrSynchronization/components/SilaeSyncField/SilaeSyncField'
import { useMemoSelector } from '@spa/redux/hooks'
import { LockInformation } from '@spa/scenes/MemberPage/components'
import { BLOCKED_DATES_INFO } from '@spa/scenes/MemberPage/components/LockInformation/LockInformation'
import { useContractTypes } from '@spa/scenes/MemberPage/constants'
import { LineManager } from '@spa/scenes/MemberPage/modals/ContractInfoFormModal/Forms/ContractForm/components/LineManager/LineManager'
import { TemporaryContractReason } from '@spa/scenes/MemberPage/modals/ContractInfoFormModal/Forms/ContractForm/components/TemporaryContractReason/TemporaryContractReason'
import { getPayPeriodsRange } from '@spa/services/contractPayrollLock'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FC } from 'react'
import { Controller as RHFController, useWatch } from 'react-hook-form'
import {
  useAccountFeatureFlags,
  useAccountPermissions
} from 'snap-redux/modules/accounts/hooks'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'
import { useAuthorizedContractTypes } from 'snap-redux/modules/countryConfigurations/selectors'
import { useRoleRights } from 'snap-redux/modules/memberships/utils'

import { TutorAsyncSelect } from '../FormFields/TutorAsyncSelect/TutorAsyncSelect'
import { InterimAdditionalFields } from '../InterimAdditionalFields/InterimAdditionalFields'

import { FieldLocationTeamCreateUser } from './components/FieldsLocationTeamCreateUser'
import { ModulateContractContainer } from './components/ModulateContractContainer'
import useNewUserForm from './hooks/useNewUserForm'
import { isContractEligibleForModulation } from './utils'

type Props = {
  formSuccess?: (value?: any) => void
  locationId?: number
  lockedPayPeriods: PayPeriodRecordMap
  teamId?: number
}
const FormNewUser: FC<Props> = ({
  formSuccess,
  locationId,
  lockedPayPeriods,
  teamId
}) => {
  const { t, tCommon } = usePlanningFormTranslation()

  const authorizedContractTypes = useAuthorizedContractTypes()
  const contractOptions = useContractTypes({ authorizedContractTypes })

  const [startDateIsInvalid, setStartDateIsInvalid] = useState(false)

  const account = useMemoSelector(getCurrentAccount)
  const { sms_enabled } = useAccountPermissions()
  const { has_dpae } = useAccountFeatureFlags()

  const canDoDpae = has_dpae ?? false
  const formRef = useRef(null)

  const countryConfigurationsQuery =
    accountService.countryConfiguration.useFetch()
  const locationQuery = locationService.location.useFetchAll({
    queryParams: {
      includes: ['current_and_future_modulation_location_contracts']
    }
  })

  const {
    control,
    setValue,
    setError,
    clearErrors,
    formLoading,
    formError,
    onSubmit,
    methods,
    formState,
    getValues,
    resetField
  } = useNewUserForm({
    accountId: account.id,
    locationId,
    teamId,
    formSuccess,
    formRef
  })

  const getLockedPayPeriodsRange = () =>
    lockedPayPeriods?.size
      ? getPayPeriodsRange(lockedPayPeriods.valueSeq().toJS())
      : null

  const lockedPayPeriodsRange = getLockedPayPeriodsRange()

  const locationIdRef = useRef(locationId)

  // Control location
  const newLocationId = useWatch({ control, name: 'location_id' })
  const newTeamId = useWatch({ control, name: 'team_id' })
  const newContratType = useWatch({ control, name: 'contract_type' })
  const newEmployeeId = useWatch({ control, name: 'employee_id' })
  const shouldDisplayTemporaryContractReason =
    newContratType === CDD &&
    (account?.get('country') === 'FR' || account?.get('country') === 'ES')

  const isEligibleForModulation = useMemo(
    () =>
      isContractEligibleForModulation(
        locationQuery?.data ?? [],
        newLocationId,
        newTeamId
      ),
    [locationQuery?.data, newLocationId, newTeamId]
  )

  useEffect(() => {
    setValue('has_modulation_period', isEligibleForModulation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEligibleForModulation])

  useEffect(() => {
    // Control the team select option in function of the location
    // Each time you switch of the location, we clean the team

    if (locationIdRef.current && newLocationId !== locationIdRef.current) {
      locationIdRef.current = newLocationId
      setValue('team_id', null as any)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newLocationId])

  // Control contract start
  const contractStart = useWatch({ control, name: 'contract_start' })

  const validateContractStart = useCallback(() => {
    const isAfterLockedPayPeriods =
      lockedPayPeriods.size &&
      moment.parseZone(contractStart).isAfter(getLockedPayPeriodsRange()?.end)

    if (!contractStart) {
      setStartDateIsInvalid(true)
      setError('contract_start', {
        type: 'focus',
        message: t('membershipCreation.validations.startDate.overlapPayPeriod')
      })
      return
    }

    if (!lockedPayPeriods.size || isAfterLockedPayPeriods) {
      setStartDateIsInvalid(false)
      clearErrors('contract_start')
      return
    }

    setStartDateIsInvalid(true)

    setError('contract_start', {
      type: 'focus',
      message: t('membershipCreation.validations.startDate.invalid')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractStart, lockedPayPeriods, t, setError, clearErrors, moment])

  useEffect(() => {
    validateContractStart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractStart, lockedPayPeriods, moment])

  const contractMustEnd = ContractTypeCanHaveEndDateArray.includes(
    newContratType as ContractTypeCanHaveEndDate
  )

  const isExecutiveOfficer =
    newContratType === EXECUTIVE_OFFICER_SELF_EMPLOYED ||
    newContratType === EXECUTIVE_OFFICER_AS_EMPLOYEE
  const isTutorAvailable =
    newContratType === INTERN || newContratType === APPRENTISSAGE

  useEffect(() => {
    if (!contractMustEnd && formState.errors.contract_end) {
      clearErrors('contract_end')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, contractMustEnd])

  const { exact } = useRoleRights()

  if (!countryConfigurationsQuery.isSuccess || !locationQuery.isSuccess) {
    return (
      <QueriesRequestsStatus
        className='m-4'
        statuses={[countryConfigurationsQuery.status, locationQuery.status]}
        errors={[countryConfigurationsQuery.error, locationQuery.error]}
      />
    )
  }

  const isFijoContractType =
    account?.get('country') === 'ES' && newContratType === 'seasonal'

  return (
    <BaseForm methods={methods} onSubmit={onSubmit}>
      <Modal.Content>
        {/* @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333) */}
        <FormError rawError={formError} />
        <Flex className='gap-4' ref={formRef}>
          <Text className='text-s font-medium uppercase text-neutral-500'>
            {t('membershipCreation.modal.infoSection')}
          </Text>

          <Controller.TextInput
            control={control}
            name='firstname'
            label={tCommon('userInfo.firstname')}
            placeholder={tCommon('userInfo.firstname')}
            required
            labelRightSection={<HRSynchronizedIcon nameInput='firstname' />}
            errorMessage={formState.errors?.firstname?.message}
            data-testid='field-input-firstname'
          />

          <RHFController
            control={control}
            name='birth_name'
            data-testid='field-input-birthname'
            render={({ field: { onChange, ref, ...otherProps } }) => {
              return (
                <TextInput
                  {...otherProps}
                  label={tCommon('userInfo.birthname')}
                  placeholder={tCommon('userInfo.birthname')}
                  required
                  labelRightSection={
                    <HRSynchronizedIcon nameInput='birth_name' />
                  }
                  data-testid='field-input-birthname'
                  errorMessage={formState.errors?.birth_name?.message}
                  inputRef={ref}
                  onChange={e => {
                    onChange(e.target.value)
                    if (!formState.touchedFields.lastname) {
                      setValue('lastname', e.target.value)
                      methods.trigger('lastname')
                    }
                    const isLastnameAndBirthnameEmpty =
                      !e.target.value && !getValues('lastname')
                    if (isLastnameAndBirthnameEmpty) {
                      resetField('lastname', { keepTouched: false })
                    }
                  }}
                />
              )
            }}
          />

          {countryConfigurationsQuery.data.show_lastname_field && (
            <RHFController
              control={control}
              name='lastname'
              data-testid='field-input-lastname'
              render={({ field: { ref, ...otherProps } }) => {
                return (
                  <TextInput
                    {...otherProps}
                    label={tCommon('userInfo.lastname')}
                    placeholder={tCommon('userInfo.lastname')}
                    required={
                      countryConfigurationsQuery.data.show_lastname_field
                    }
                    labelRightSection={
                      <HRSynchronizedIcon nameInput='lastname' />
                    }
                    data-testid='field-input-lastname'
                    errorMessage={formState.errors?.lastname?.message}
                    inputRef={ref}
                  />
                )
              }}
            />
          )}

          <Flex>
            {!account.automatically_generate_employee_ids && (
              <InputWrap
                field={
                  <Controller.TextInput
                    control={control as any}
                    name='employee_id'
                    id='employee_id'
                    placeholder={tCommon('userInfo.employeeId.label')}
                    data-testid='field-input-lastname'
                    disabled={account.automatically_generate_employee_ids}
                  />
                }
                className='w-full'
                labelRightSection={
                  <Flex row className='grow items-center'>
                    <MatriculInfoIcon />
                    <HRSynchronizedIcon nameInput='employee_id' />
                    <Flex className='grow'>
                      <Anchor
                        href={tCommon('userInfo.employeeId.automaticIdLink')}
                        className='text-right'
                        target='_blank'
                        rightIcon
                      >
                        {tCommon('userInfo.employeeId.automaticId')}
                      </Anchor>
                    </Flex>
                  </Flex>
                }
                label={tCommon('userInfo.employeeId.label')}
              />
            )}
            <SilaeSyncFieldId
              disabledSilaeSync={
                !account.automatically_generate_employee_ids &&
                !newEmployeeId.length
              }
              locationIdForSilaeSync={newLocationId}
            />
          </Flex>

          <Controller.DatePickerInput
            control={control}
            name='birth_date'
            label={tCommon('userInfo.birthdateLabel')}
            labelRightSection={<HRSynchronizedIcon nameInput='birth_date' />}
            errorMessage={formState.errors?.birth_date?.message}
          />

          <Controller.TextInput
            control={control}
            id='email'
            name='email'
            label={tCommon('userInfo.email')}
            placeholder={tCommon('userInfo.email')}
            labelRightSection={<HRSynchronizedIcon nameInput='email' />}
            desc={t('membershipCreation.emailDesc')}
            data-testid='field-email'
            errorMessage={formState.errors?.email?.message}
          />

          <Controller.PhoneInput
            id='mobile_phone'
            control={control}
            name='mobile_phone'
            data-testid='field-mobile-phone'
            label={
              <Flex row className='items-center'>
                <FieldLabel
                  className='!mb-0'
                  label={tCommon('userInfo.mobilePhone')}
                />
                <HRSynchronizedIcon nameInput='mobile_phone' />
              </Flex>
            }
            errorMessage={formState.errors?.mobile_phone?.message}
          />

          {account?.employee_can_receive_sms_notification && sms_enabled && (
            <InputWrap
              fillWidth
              htmlFor='sms_notification_enabled'
              field={
                <Controller.Switch
                  control={control}
                  id='sms_notification_enabled'
                  name='sms_notification_enabled'
                  size='s'
                  data-testid='field-sms-notification-enabled'
                  position='right'
                />
              }
              labelRightSection={
                <Tooltip
                  label={tCommon(
                    'userInfo.smsNotificationTooltipAvoidedIfPushNotification'
                  )}
                >
                  <IconWrap icon={<InformationCircleBoldIcon />} />
                </Tooltip>
              }
              label={tCommon('userInfo.smsNotification')}
            />
          )}

          <Controller.PhoneInput
            id='fixed_phone'
            name='fixed_phone'
            control={control}
            data-testid='field-fixed-phone'
            label={
              <Flex row className='items-center'>
                <FieldLabel
                  className='!mb-0'
                  label={tCommon('userInfo.fixedPhone')}
                />
                <HRSynchronizedIcon nameInput='fixed_phone' />
              </Flex>
            }
            errorMessage={formState.errors?.fixed_phone?.message}
          />

          <Text className='mt-5 text-s font-medium uppercase text-neutral-500'>
            {t('membershipCreation.modal.contractSection')}
          </Text>

          <Controller.DatePickerInput
            control={control}
            maxLevel='year'
            name='contract_start'
            label={t('membershipCreation.startDate.label')}
            labelRightSection={
              <HRSynchronizedIcon nameInput='contract_start' />
            }
            errorMessage={formState.errors?.contract_start?.message}
          />

          {/* replacing start_hour and start_min by contract_hour */}
          {canDoDpae && !isExecutiveOfficer && (
            <Controller.TextInput
              control={control}
              id='contract_hour'
              name='contract_hour'
              type='time'
              errorMessage={formState.errors?.contract_hour?.message}
              label={t('membershipCreation.startHour.label')}
              required
              labelRightSection={
                <HRSynchronizedIcon nameInput='contract_hour' />
              }
            />
          )}

          {startDateIsInvalid && (
            <LockInformation
              type={BLOCKED_DATES_INFO}
              payPeriodsStart={lockedPayPeriodsRange?.start}
              payPeriodsEnd={lockedPayPeriodsRange?.end}
            />
          )}

          <Controller.Select
            control={control}
            options={contractOptions}
            name='contract_type'
            placeholder={t('membershipCreation.type.label')}
            label={t('membershipCreation.type.label')}
            data-testid='field-contract_type'
            errorMessage={formState.errors?.contract_type?.message}
            clearable
            labelRightSection={<HRSynchronizedIcon nameInput='contract_type' />}
          />

          {isTutorAvailable && (
            <TutorAsyncSelect
              label={<FieldLabel label={t('membershipCreation.tutor.label')} />}
              locationId={newLocationId}
              teamId={newTeamId}
            />
          )}

          {shouldDisplayTemporaryContractReason && <TemporaryContractReason />}

          <InterimAdditionalFields />

          {contractMustEnd && (
            <Controller.DatePickerInput
              control={control}
              maxLevel='year'
              name='contract_end'
              errorMessage={formState.errors?.contract_end?.message}
              label={t('membershipCreation.endDate.label')}
              required={!isFijoContractType}
              labelRightSection={
                <HRSynchronizedIcon nameInput='contract_end' />
              }
            />
          )}

          {!isExecutiveOfficer && (
            <Controller.NumberInput
              control={control}
              name='contract_time'
              label={t('membershipCreation.time.label')}
              placeholder='5'
              min={0}
              data-testid='field-contract_time'
              errorMessage={formState.errors?.contract_time?.message}
              labelRightSection={
                <HRSynchronizedIcon nameInput='contract_time' />
              }
            />
          )}

          <FieldLocationTeamCreateUser />

          {!isExecutiveOfficer && (
            <LineManager teamId={newTeamId} locationId={newLocationId} />
          )}

          {isEligibleForModulation && <ModulateContractContainer />}
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Flex className='w-full gap-3'>
          {!account.get('manager_created_employees_plannable') &&
            exact.manager && (
              <Alert switchAt='sm'>
                {t('membershipCreation.nonPlannableEmployee')}
              </Alert>
            )}

          <Modal.Footer.Success
            type='submit'
            loading={formLoading}
            disabled={Boolean(Object.keys(formState.errors).length)}
            variant='primary'
            intent='default'
            fillWidth
          >
            {tCommon('action.save')}
          </Modal.Footer.Success>
        </Flex>
      </Modal.Footer>
    </BaseForm>
  )
}

export default FormNewUser
